import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Box,
    CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from '../../services/api';
import type { User } from '../../services/types/auth';
import { useTranslation } from 'react-i18next';

interface ContentCreatorFormData {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phone: string;
}

const initialFormData: ContentCreatorFormData = {
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
};

const ContentCreators: React.FC = () => {
    const [creators, setCreators] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState<ContentCreatorFormData>(initialFormData);
    const [error, setError] = useState<string | null>(null);
    const { t } = useTranslation();

    useEffect(() => {
        fetchContentCreators();
    }, []);

    const fetchContentCreators = async () => {
        try {
            const response = await axios.get('/admin/users/content-creators');
            setCreators(response.data);
        } catch (error) {
            console.error('Failed to fetch content creators:', error);
            setError(t('admin.contentCreators.errors.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        
        try {
            await axios.post('/admin/users/content-creators', formData);
            setOpenDialog(false);
            setFormData(initialFormData);
            fetchContentCreators();
        } catch (error: any) {
            setError(error.response?.data?.error || t('admin.contentCreators.errors.createFailed'));
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h4">{t('admin.contentCreators.title')}</Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => setOpenDialog(true)}
                        >
                            {t('admin.contentCreators.addButton')}
                        </Button>
                    </Box>
                </Grid>

                {error && (
                    <Grid item xs={12}>
                        <Typography color="error">{error}</Typography>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('admin.contentCreators.table.name')}</TableCell>
                                    <TableCell>{t('admin.contentCreators.table.email')}</TableCell>
                                    <TableCell>{t('admin.contentCreators.table.phone')}</TableCell>
                                    <TableCell align="center">{t('admin.contentCreators.table.status')}</TableCell>
                                    <TableCell align="center">{t('admin.contentCreators.table.lastLogin')}</TableCell>
                                    <TableCell>{t('admin.contentCreators.table.actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {creators.map((creator) => (
                                    <TableRow key={creator.user_id}>
                                        <TableCell>
                                            {creator.first_name} {creator.last_name}
                                        </TableCell>
                                        <TableCell>{creator.email}</TableCell>
                                        <TableCell>{creator.phone}</TableCell>
                                        <TableCell align="center">
                                            {creator.is_active ? t('admin.contentCreators.table.active') : t('admin.contentCreators.table.inactive')}
                                        </TableCell>
                                        <TableCell align="center">
                                            {creator.last_login ? new Date(creator.last_login).toLocaleDateString('en-GB', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric'
                                            }) : t('admin.contentCreators.table.never')}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton size="small" color="primary">
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton size="small" color="error">
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <form onSubmit={handleSubmit}>
                    <DialogTitle>{t('admin.contentCreators.form.title')}</DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="first_name"
                                    label={t('admin.contentCreators.form.firstName')}
                                    value={formData.first_name}
                                    onChange={handleInputChange}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="last_name"
                                    label={t('admin.contentCreators.form.lastName')}
                                    value={formData.last_name}
                                    onChange={handleInputChange}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="email"
                                    label={t('admin.contentCreators.form.email')}
                                    type="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="password"
                                    label={t('admin.contentCreators.form.password')}
                                    type="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="phone"
                                    label={t('admin.contentCreators.form.phone')}
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDialog(false)}>{t('admin.contentCreators.form.cancel')}</Button>
                        <Button type="submit" variant="contained" color="primary">
                            {t('admin.contentCreators.form.add')}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Container>
    );
};

export default ContentCreators;
